/**
 * API волны W2 — база знаний: популярные запросы, networkAckPct стандартов,
 * «Напомнить неподтвердившим», AI course-outline / draft / feedback, Диск → статья.
 *
 * Правила (план wk6 §4): lib/wikiApi.ts заморожен после фазы 0 — новые вызовы
 * и аддитивные DTO волна кладёт только в свой модуль. Клиент — `api` из
 * '../api'. Существующие DTO расширяются пересечением.
 *
 * `CourseOutline` + `kbApi.courseOutline` — общий контракт с W4 (мастер курса
 * импортирует отсюда, не дублирует).
 */
import { api } from '../api';
import type {
  StandardItemDto,
  StandardsResponseDto,
  WikiArticleDetailDto,
  WikiArticleListItem,
  WikiBlockDto,
} from '../wikiApi';

// ── Аддитивные поля к существующим DTO ───────────────────────────────

/** Карточка статьи в списке: + просмотры, владелец, проверка актуальности. */
export type KbArticleListItem = WikiArticleListItem & {
  viewCount?: number;
  ownerName?: string | null;
  verifiedAt?: string | null;
  updatedAt?: string | null;
};

/** Раздел: + описание (wiki_blocks.description, может быть пустым). */
export type KbBlock = WikiBlockDto & { description?: string | null };

/** «Подтвердили по сети» (приходит только kb:admin). */
export interface NetworkAck {
  /** 0..100; null — у стандарта нет аудитории среди пользователей CRM. */
  pct: number | null;
  acked: number;
  audience: number;
}

/** Статья: + курсы по теме и сетевой % подтверждения стандарта. */
export type KbArticleDetail = WikiArticleDetailDto & {
  relatedCourses?: Array<{ id: string; name: string }>;
  networkAck?: NetworkAck | null;
};

/** Стандарт в списке: + % подтверждения по сети (kb:admin). */
export type KbStandardItem = StandardItemDto & { networkAckPct?: number | null };
export type KbStandardsResponse = Omit<StandardsResponseDto, 'items'> & { items: KbStandardItem[] };

/** Итог «Напомнить неподтвердившим». */
export interface StandardRemindResult {
  sent: number;
  audience: number;
  pending: number;
  /** Сегодня уже напоминали — повтор не отправлен (раз в сутки по МСК). */
  alreadyToday: boolean;
}

// ── AI: план курса (общий контракт W2 ↔ W4) ──────────────────────────

export type OutlineLessonType = 'article' | 'video' | 'quiz' | 'practical';

export interface CourseOutlineLesson {
  type: OutlineLessonType;
  title: string;
  /** Ориентировочные минуты (1..120). */
  min: number;
}

export interface CourseOutlineModule {
  title: string;
  lessons: CourseOutlineLesson[];
}

/** Черновик структуры курса (POST /wiki/ai/course-outline). Ничего не сохраняет. */
export interface CourseOutline {
  title: string;
  description: string;
  modules: CourseOutlineModule[];
  /** false — AI недоступен/ответил мусором, план собран по заголовкам статьи. */
  aiUsed: boolean;
  sourceSlug: string | null;
}

export interface ArticleDraftResult {
  bodyMd: string;
  aiUsed: boolean;
  note: string | null;
}

export interface DiskUsage {
  count: number;
  articles: Array<{ slug: string; title: string }>;
  /** Статья, чьё вложение зеркалировано в этот файл: удаление файла на Диске уберёт и его вложение. */
  attachmentOf?: { slug: string; title: string } | null;
}

export const kbApi = {
  /** «Часто ищут» в герое БЗ (≥2 разных пользователей, 30 дней). */
  popularQueries: (limit = 6) => api.get<Array<{ query: string; count: number }>>(`/wiki/search/popular?limit=${limit}`),
  /** Стандарты с networkAckPct (для kb:admin; остальным поле не приходит). */
  standards: () => api.get<KbStandardsResponse>('/wiki/standards'),
  /** «Напомнить неподтвердившим» (kb:admin, раз в сутки на стандарт). */
  remindStandard: (articleId: string) =>
    api.post<StandardRemindResult>(`/wiki/standards/items/${encodeURIComponent(articleId)}/remind`),
  /** План курса из статьи или по теме (kb:create | kb:admin). */
  courseOutline: (body: { articleSlug?: string; topic?: string }) => api.post<CourseOutline>('/wiki/ai/course-outline', body),
  /** AI-черновик новой статьи по заголовку и тезисам (kb:create). */
  articleDraft: (body: { title: string; points: string[]; type?: string }) => api.post<ArticleDraftResult>('/wiki/ai/draft', body),
  /** «Помогло / Не то» под ответом AI-помощника. */
  aiFeedback: (body: { helpful: boolean; query: string; sources?: string[]; comment?: string }) =>
    api.post<{ ok: true }>('/wiki/ai/feedback', body),
  /** В каких статьях есть ссылка на файл диска. */
  diskUsage: (fileId: string) => api.get<DiskUsage>(`/wiki/disk/files/${encodeURIComponent(fileId)}/usage`),
};

/** Файл Диска, выбранный в окне «Файл с Диска» (редактор статьи). */
export interface PickedDiskFile {
  id: string;
  fileName: string;
  /** Размер в байтах (для карточки файла). */
  size: number;
}

/** Адрес скачивания файла Диска — тот же, что в карточке файла статьи (`fkFile.url`). */
export function diskFileUrl(id: string): string {
  return `/api/wiki/disk/files/${encodeURIComponent(id)}/download`;
}

/** Markdown-ссылка на файл диска для вставки в статью («В статью»). */
export function diskFileMarkdownLink(file: { id: string; fileName: string }): string {
  const name = file.fileName.replace(/[[\]]/g, '');
  return `[${name}](${diskFileUrl(file.id)})`;
}

/** Та же ссылка в HTML — для text/html буфера: редактор вставит её настоящей ссылкой, а не текстом. */
export function diskFileHtmlLink(file: { id: string; fileName: string }): string {
  const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  return `<a href="${esc(diskFileUrl(file.id))}">${esc(file.fileName)}</a>`;
}

/**
 * «В статью»: кладёт ссылку в буфер сразу в двух видах — text/html (ссылка) и text/plain
 * (markdown, как раньше). Если ClipboardItem недоступен (старый браузер, не-https) —
 * запасной путь writeText с markdown. Возвращает false, если буфер недоступен совсем.
 */
export async function copyDiskFileLink(file: { id: string; fileName: string }): Promise<boolean> {
  const md = diskFileMarkdownLink(file);
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([diskFileHtmlLink(file)], { type: 'text/html' }),
          'text/plain': new Blob([md], { type: 'text/plain' }),
        }),
      ]);
      return true;
    }
  } catch {
    /* ClipboardItem отказал (права, формат) — пробуем простой текст */
  }
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(md);
    return true;
  } catch {
    return false;
  }
}

// ── Чистые помощники экранов БЗ (покрыты kb.test.ts) ─────────────────

/** «12 окт» / «12 окт 2025» (год — только если не текущий). Пусто → ''. */
export function kbShortDate(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('ru-RU', opts).replace(/\.$/, '').replace(' г.', '');
}

/**
 * Актуальность статьи для подписи «Проверено …»: ok — в сроке, due — срок
 * проверки истёк, none — проверка не настроена.
 */
export function kbFreshness(
  verifiedAt: string | null | undefined,
  verifyEveryDays: number | null | undefined,
  now: number = Date.now(),
): 'ok' | 'due' | 'none' {
  if (verifyEveryDays == null) return verifiedAt ? 'ok' : 'none';
  if (!verifiedAt) return 'due';
  const t = new Date(verifiedAt).getTime();
  if (Number.isNaN(t)) return 'due';
  return t + verifyEveryDays * 86_400_000 >= now ? 'ok' : 'due';
}

/** Цвет полоски «Подтвердили по сети»: ≥90 зелёный, ≥60 оранжевый, иначе красный. */
export function kbAckColor(pct: number | null | undefined): string {
  if (pct == null) return 'var(--fk-border-strong)';
  if (pct >= 90) return 'var(--fk-success)';
  if (pct >= 60) return 'var(--fk-orange)';
  return 'var(--fk-error)';
}

/**
 * Подсказка «Похоже, ответ уже есть» в вопросе УК: из текста берём слова
 * от 3 букв (без стоп-слов) — поисковая строка для searchSuggest.
 * Короткий текст → '' (не ищем).
 */
export function kbHintQuery(text: string): string {
  const STOP = new Set([
    'как', 'что', 'где', 'кто', 'для', 'при', 'это', 'или', 'мне', 'нам', 'вас', 'про', 'над', 'под', 'без', 'уже', 'еще', 'ещё', 'все', 'всё', 'так', 'там', 'тут',
    'после', 'перед', 'когда', 'какой', 'какая', 'какие', 'можно', 'нужно', 'почему', 'сколько', 'чтобы', 'если', 'есть', 'этот', 'этого', 'который',
    'делать', 'надо', 'подскажите', 'пожалуйста', 'здравствуйте', 'добрый', 'день',
  ]);
  const words = (text.toLowerCase().match(/[a-zа-яё0-9]{3,}/gi) ?? []).filter((w) => !STOP.has(w));
  const uniq = [...new Set(words)].slice(0, 5);
  return uniq.length >= 1 && text.trim().length >= 12 ? uniq.join(' ') : '';
}
